import React, { Fragment, useCallback, useState } from 'react'
import { toast } from 'sonner'
import PublishIcon from '@material-ui/icons/Publish'
import { Modal, Typography } from '@material-ui/core'
import Dropzone from 'react-dropzone'
import Papa from 'papaparse'
import globalStyles from '../style.module.css'
import {
	bulkValidateRegistrationCertificates,
	bulkCommitRegistrationCertificates,
} from '../api'
import { useRegistrationCertificatesStore } from '../stores/registrationCertificateStore'
import Button from '../../../generic/Button'
import { ManualDateInputModal } from './ManualDateInputModal'
import { getSelectedClients } from '../../../data/interactions'

export const BulkUploadCertificatesModal = ({ uploadSuccessCallback }) => {
	// Certificates are listed across all partners, but they are still uploaded
	// to the partner selected in the menu.
	const selectedClient = getSelectedClients()[0]

	const {
		bulkUploadModalOpen,
		setBulkUploadModalOpen,
		setManualDateInputModalOpen,
		setCertificatesNeedingDates,
		certificatesNeedingDates,
	} = useRegistrationCertificatesStore()

	const [uploading, setUploading] = useState(false)
	const [file, setFile] = useState(null)
	const [parsedData, setParsedData] = useState(null)
	const [headers, setHeaders] = useState([])
	const [step, setStep] = useState('upload') // 'upload', 'mapping', 'validating'
	const [delimiter, setDelimiter] = useState(',') // Default to comma

	// Column mapping state
	const [certNumberColumn, setCertNumberColumn] = useState('')
	const [imageIdColumn, setImageIdColumn] = useState('')
	const [dateColumn, setDateColumn] = useState('')

	// Validated data
	const [validatedCertificates, setValidatedCertificates] = useState([])
	const [validationErrors, setValidationErrors] = useState([])

	const handleClose = () => {
		setFile(null)
		setParsedData(null)
		setHeaders([])
		setStep('upload')
		setDelimiter(',')
		setCertNumberColumn('')
		setImageIdColumn('')
		setDateColumn('')
		setValidatedCertificates([])
		setValidationErrors([])
		setBulkUploadModalOpen(false)
		setUploading(false)
	}

	const parseCSV = useCallback((csvFile, selectedDelimiter) => {
		// Parse CSV
		Papa.parse(csvFile, {
			header: true,
			skipEmptyLines: true,
			delimiter: selectedDelimiter,
			quoteChar: '"',
			escapeChar: '"',
			complete: (results) => {
				if (results.data.length === 0) {
					toast.error('CSV file is empty')
					return
				}

				if (results.data.length > 100000) {
					toast.error('CSV exceeds maximum of 100,000 rows')
					setFile(null)
					return
				}

				setHeaders(results.meta.fields || [])
				setParsedData(results.data)
				setStep('mapping')
			},
			error: (error) => {
				toast.error('Error parsing CSV: ' + error.message)
				setFile(null)
			},
		})
	}, [])

	const onDrop = useCallback((acceptedFiles) => {
		const csvFile = acceptedFiles[0]
		setFile(csvFile)
	}, [])

	const handleParse = () => {
		if (file) {
			parseCSV(file, delimiter)
		}
	}

	const normalizeDate = (dateStr) => {
		if (!dateStr || dateStr.trim() === '') return null

		try {
			const date = new Date(dateStr)
			if (isNaN(date.getTime())) return null

			// Format as YYYY-MM-DD
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			return `${year}-${month}-${day}`
		} catch (e) {
			return null
		}
	}

	const handleValidate = async () => {
		setUploading(true)
		setStep('validating')
		setValidationErrors([]) // Clear previous errors

		try {
			// Build certificates array from parsed data
			const certificates = []
			const seenRows = new Set()

			for (const row of parsedData) {
				const certNumberRaw = row[headers[certNumberColumn]]
				const imageIdRaw = row[headers[imageIdColumn]]
				const certNumber = certNumberRaw ? certNumberRaw.trim() : ''
				const imageId = imageIdRaw ? imageIdRaw.trim() : ''
				const dateValue = dateColumn !== '' ? row[headers[dateColumn]] : null

				if (!certNumber || !imageId) continue

				const normalizedDate = normalizeDate(dateValue)

				// Create unique key for deduplication
				const rowKey = `${certNumber}|${imageId}|${normalizedDate || ''}`

				// Skip exact duplicates
				if (seenRows.has(rowKey)) continue
				seenRows.add(rowKey)

				certificates.push({
					registrationCertificateNumber: certNumber,
					partnerImageId: imageId,
					effectiveRegistrationDate: normalizedDate,
				})
			}

			// Check for conflicting dates (same cert number with different dates)
			const certDateMap = new Map()
			for (const cert of certificates) {
				const certNum = cert.registrationCertificateNumber
				const date = cert.effectiveRegistrationDate

				if (certDateMap.has(certNum)) {
					const existingDate = certDateMap.get(certNum)
					if (existingDate !== date && existingDate !== null && date !== null) {
						toast.error(
							`Error: Certificate ${certNum} has conflicting dates. Please ensure all rows for the same certificate have the same date.`,
						)
						setUploading(false)
						setStep('mapping')
						return
					}
				} else {
					certDateMap.set(certNum, date)
				}
			}

			// Call validate endpoint
			const response = await bulkValidateRegistrationCertificates(certificates)

			console.log(response)

			// Check if response is a raw Response object (error case)
			if (!response || response.ok === false) {
				const errorMsg =
					response && response.message
						? response.message
						: 'Validation failed. Please check your data and try again.'
				toast.error(errorMsg)
				setUploading(false)
				setStep('mapping')
				return
			}

			// Check for validation errors in the response
			if (response.errors && response.errors.length > 0) {
				setValidationErrors(response.errors)
				toast.error(
					`Validation failed: ${response.errors.length} errors found. See details below.`,
				)
				setUploading(false)
				setStep('mapping')
				return
			}

			if (response.needsManualDates && response.needsManualDates.length > 0) {
				// Show manual date input modal
				setCertificatesNeedingDates(response.needsManualDates)
				setValidatedCertificates(response.success || [])
				setManualDateInputModalOpen(true)
				setUploading(false)
			} else {
				// All dates found, proceed to commit
				await handleCommit(response.success || [])
			}
		} catch (error) {
			console.error('Validation error:', error)
			const errorMessage =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				'Validation failed. Please try again.'
			toast.error(errorMessage)
		} finally {
			// Always reset state on error or completion
			if (step === 'validating') {
				setUploading(false)
				setStep('mapping')
			}
		}
	}

	const handleCommit = async (certificatesToCommit) => {
		if (!certificatesToCommit || certificatesToCommit.length === 0) {
			toast.error('No certificates to upload')
			setUploading(false)
			return
		}

		setUploading(true)

		try {
			const response =
				await bulkCommitRegistrationCertificates(certificatesToCommit)

			console.log(response)
			// Check if response is a raw Response object (error case)
			if (!response || response.ok === false) {
				const errorMsg =
					response && response.message
						? response.message
						: 'Upload failed. Please try again.'
				toast.error(errorMsg)
				setStep('mapping')
				setUploading(false)
				return
			}

			toast.success(
				`Successfully uploaded ${certificatesToCommit.length} certificate associations`,
			)
			handleClose()
			uploadSuccessCallback()
		} catch (error) {
			console.error('Commit error:', error)
			const errorMessage =
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				'Upload failed. Please try again.'
			toast.error(errorMessage)
			setUploading(false)
		}
	}

	const handleManualDatesSubmit = async (manualDates) => {
		// Merge manual dates with validated certificates
		const allCertificates = [...validatedCertificates]

		// Add certificates with manual dates
		for (const [certNumber, date] of Object.entries(manualDates)) {
			const needsDate = certificatesNeedingDates.find(
				(c) => c.registrationCertificateNumber === certNumber,
			)
			if (needsDate) {
				for (const imageId of needsDate.partnerImageIds) {
					allCertificates.push({
						registrationCertificateNumber: certNumber,
						partnerImageId: imageId,
						effectiveRegistrationDate: date,
					})
				}
			}
		}

		await handleCommit(allCertificates)
	}

	const canProceedToValidation =
		certNumberColumn !== '' && imageIdColumn !== '' && parsedData

	const uniqueCertCount = parsedData
		? new Set(
				parsedData.map((row) => {
					const val = row[headers[certNumberColumn]]
					return val ? val.trim() : ''
				}),
			).size
		: 0

	return (
		<Fragment>
			<Modal open={bulkUploadModalOpen} onClose={handleClose}>
				<div
					style={{
						backgroundColor: 'white',
						padding: '20px',
						width: '70vw',
						maxHeight: '90vh',
						overflow: 'auto',
						margin: 'auto',
						marginTop: '5vh',
					}}
				>
					<h3>
						Bulk Upload Registration Certificates
						{selectedClient ? ` to ${selectedClient.name}` : ''}
					</h3>
					<br />

					{step === 'upload' && (
						<>
							<Typography variant='body2'>
								Upload a CSV file with registration certificate data
							</Typography>
							<br />
							<Dropzone
								multiple={false}
								accept='.csv'
								onDrop={onDrop}
								acceptStyle={{ opacity: 0.3 }}
								rejectStyle={{ background: '' }}
								style={{
									border: '3px dashed #ddd',
									padding: '50px 0 30px 0',
									textAlign: 'center',
									height: '200px',
									width: '100%',
								}}
							>
								<PublishIcon />
								{file ? (
									<>
										<br />
										<Typography variant='body2'>File selected</Typography>
										<br />
										<Typography variant='body2'>{file.name}</Typography>
									</>
								) : (
									<>
										<br />
										<Typography variant='body2'>Drop CSV file here</Typography>
										<br />
										<Typography variant='body2'>Supports: .CSV</Typography>
									</>
								)}
							</Dropzone>

							{file && (
								<>
									<br />
									<Typography variant='body2'>Select Delimiter</Typography>
									<select
										className={globalStyles.searchInput}
										value={delimiter}
										onChange={(e) => setDelimiter(e.target.value)}
										style={{ width: '100%', marginTop: '5px' }}
									>
										<option value=','>Comma (,)</option>
										<option value=';'>Semicolon (;)</option>
									</select>
									<br />
									<Button onClick={handleParse} style={{ marginTop: '10px' }}>
										Parse CSV
									</Button>
								</>
							)}
						</>
					)}

					{step === 'mapping' && (
						<>
							<Typography variant='body2'>
								Map CSV columns to required fields
							</Typography>
							<br />
							<Typography variant='body2' style={{ marginBottom: '10px' }}>
								Total rows: {parsedData.length} | Unique certificates:{' '}
								{uniqueCertCount}
							</Typography>

							<div style={{ marginBottom: '20px' }}>
								<Typography variant='body2'>
									Registration Certificate Number (required)
								</Typography>
								<select
									className={globalStyles.searchInput}
									value={certNumberColumn}
									onChange={(e) => setCertNumberColumn(e.target.value)}
									style={{ width: '100%', marginTop: '5px' }}
								>
									<option value=''>Select column...</option>
									{headers.map((header, index) => (
										<option key={index} value={index}>
											Column {index}: {header}
										</option>
									))}
								</select>
							</div>

							<div style={{ marginBottom: '20px' }}>
								<Typography variant='body2'>
									Partner Image ID (required)
								</Typography>
								<select
									className={globalStyles.searchInput}
									value={imageIdColumn}
									onChange={(e) => setImageIdColumn(e.target.value)}
									style={{ width: '100%', marginTop: '5px' }}
								>
									<option value=''>Select column...</option>
									{headers.map((header, index) => (
										<option key={index} value={index}>
											Column {index}: {header}
										</option>
									))}
								</select>
							</div>

							<div style={{ marginBottom: '20px' }}>
								<Typography variant='body2'>
									Effective Registration Date (optional)
								</Typography>
								<select
									className={globalStyles.searchInput}
									value={dateColumn}
									onChange={(e) => setDateColumn(e.target.value)}
									style={{ width: '100%', marginTop: '5px' }}
								>
									<option value=''>Select column...</option>
									{headers.map((header, index) => (
										<option key={index} value={index}>
											Column {index}: {header}
										</option>
									))}
								</select>
							</div>

							<Typography variant='body2' style={{ marginTop: '20px' }}>
								Preview (first 5 rows):
							</Typography>
							<div
								style={{
									maxHeight: '200px',
									overflow: 'auto',
									border: '1px solid #ddd',
									padding: '10px',
									marginTop: '10px',
								}}
							>
								<table style={{ width: '100%', fontSize: '12px' }}>
									<thead>
										<tr>
											{headers.map((header, index) => (
												<th key={index} style={{ textAlign: 'left' }}>
													{index}: {header}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{parsedData.slice(0, 5).map((row, rowIndex) => (
											<tr key={rowIndex}>
												{headers.map((header, colIndex) => (
													<td key={colIndex}>{row[header]}</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</>
					)}

					{validationErrors.length > 0 && step === 'mapping' && (
						<div style={{ marginTop: '20px' }}>
							<Typography variant='h6' style={{ color: '#d32f2f' }}>
								Validation Errors ({validationErrors.length})
							</Typography>
							<Typography
								variant='body2'
								style={{ marginTop: '5px', marginBottom: '10px' }}
							>
								Showing first {Math.min(validationErrors.length, 50)} errors
							</Typography>
							<div
								style={{
									maxHeight: '200px',
									overflow: 'auto',
									border: '1px solid #d32f2f',
									padding: '10px',
									backgroundColor: '#ffebee',
								}}
							>
								<table style={{ width: '100%', fontSize: '12px' }}>
									<thead>
										<tr>
											<th style={{ textAlign: 'left', padding: '5px' }}>
												Certificate Number
											</th>
											<th style={{ textAlign: 'left', padding: '5px' }}>
												Partner Image ID
											</th>
											<th style={{ textAlign: 'left', padding: '5px' }}>
												Error
											</th>
										</tr>
									</thead>
									<tbody>
										{validationErrors.slice(0, 50).map((error, index) => (
											<tr key={index}>
												<td style={{ padding: '5px' }}>
													{error.registrationCertificateNumber}
												</td>
												<td style={{ padding: '5px' }}>
													{error.partnerImageId}
												</td>
												<td style={{ padding: '5px' }}>{error.error}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}

					{step === 'validating' && (
						<div style={{ textAlign: 'center', padding: '50px' }}>
							<Typography variant='body2'>
								Validating certificates...
							</Typography>
							<Typography variant='body2' style={{ marginTop: '10px' }}>
								Please wait...
							</Typography>
						</div>
					)}

					<div style={{ display: 'flex', marginTop: '20px', gap: '10px' }}>
						<Button onClick={handleClose} disabled={uploading}>
							Cancel
						</Button>
						{step === 'mapping' && (
							<Button
								onClick={handleValidate}
								disabled={!canProceedToValidation || uploading}
							>
								Validate & Upload
							</Button>
						)}
					</div>
				</div>
			</Modal>

			<ManualDateInputModal onSubmit={handleManualDatesSubmit} />
		</Fragment>
	)
}
